'use client';

import { Button } from '../../components/button';
import { LegacyDialog } from '../../components/dialog';
import { IconButton } from '../../components/icon-button';
import { Box, HStack, VStack } from '@terra/ui-styled-system';
import { Cancel01Icon } from '@terra/ui-icons';
import { useTranslations } from 'next-intl';
import type { ReactNode } from 'react';
import { automationId } from '@terra/shared-utils';
import { useFooterScrollShadow } from '../../hooks/useFooterScrollShadow';

export type SettingsEditDialogProps = {
  open: boolean;
  title: string;
  description?: string;
  children: ReactNode;
  onOpenChange: (open: boolean) => void;
  onSubmit: () => void | Promise<void>;
  loading?: boolean;
  submitText?: string;
  cancelText?: string;
  isSubmitDisabled?: boolean;
  noInitialFocus?: boolean;
  contentMaxW?: string;
  contentMaxHeight?: string;
  contentAutomationId?: string;
  submitAutomationId?: string;
  unmountOnExit?: boolean;
  withFooterScrollShadow?: boolean;
};
export function SettingsEditDialog({
  open,
  title,
  description,
  children,
  onOpenChange,
  onSubmit,
  loading,
  submitText,
  cancelText,
  isSubmitDisabled,
  noInitialFocus,
  contentMaxW = '520px',
  contentMaxHeight,
  contentAutomationId,
  submitAutomationId,
  unmountOnExit,
  withFooterScrollShadow = false,
}: SettingsEditDialogProps) {
  const tCommon = useTranslations('designSystem.common');
  const { scrollableRef, scrollContentRef, boxShadow, updateScrollState } =
    useFooterScrollShadow();
  const resolvedSubmitText = submitText ?? tCommon('save');
  const resolvedCancelText = cancelText ?? tCommon('cancel');

  return (
    <LegacyDialog.Root
      open={open}
      onOpenChange={({ open: nextOpen }) => onOpenChange(nextOpen)}
      {...(noInitialFocus && { initialFocusEl: () => null })}
      {...(unmountOnExit && { lazyMount: true, unmountOnExit: true })}
    >
      <LegacyDialog.Backdrop />
      <LegacyDialog.Positioner>
        <LegacyDialog.Content
          maxW={contentMaxW}
          w="full"
          maxH="90vh"
          display="flex"
          {...(contentAutomationId ? automationId(contentAutomationId) : {})}
        >
          <VStack alignItems="stretch" gap={6} p={6} w="full" minH={0}>
            <VStack alignItems="flex-start" gap={1} pr={8} flexShrink={0}>
              <LegacyDialog.Title>{title}</LegacyDialog.Title>
              {description ? (
                <LegacyDialog.Description color="fg.neutral.muted">
                  {description}
                </LegacyDialog.Description>
              ) : null}
            </VStack>
            <Box
              w="full"
              minH={0}
              ref={withFooterScrollShadow ? scrollableRef : undefined}
              onScroll={withFooterScrollShadow ? updateScrollState : undefined}
              {...(contentMaxHeight
                ? { maxH: contentMaxHeight, overflowY: 'auto' }
                : {})}
            >
              {withFooterScrollShadow ? (
                <Box ref={scrollContentRef}>{children}</Box>
              ) : (
                children
              )}
            </Box>
            <HStack
              justifyContent="flex-end"
              gap={3}
              flexShrink={0}
              style={withFooterScrollShadow ? { boxShadow } : undefined}
            >
              <Button
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={loading}
              >
                {resolvedCancelText}
              </Button>
              <Button
                onClick={onSubmit}
                loading={loading}
                disabled={loading || isSubmitDisabled}
                {...(submitAutomationId
                  ? automationId(submitAutomationId)
                  : {})}
              >
                {resolvedSubmitText}
              </Button>
            </HStack>
          </VStack>
          <LegacyDialog.CloseTrigger
            asChild
            position="absolute"
            top="2"
            right="2"
          >
            <IconButton
              aria-label={tCommon('close')}
              variant="ghost"
              size="sm"
              disabled={loading}
            >
              <Cancel01Icon size={16} />
            </IconButton>
          </LegacyDialog.CloseTrigger>
        </LegacyDialog.Content>
      </LegacyDialog.Positioner>
    </LegacyDialog.Root>
  );
}
